// src/components/forms/RegisterForm.tsx
import { Form, Input, Button, Tooltip, message, Segmented } from 'antd';
import type { Rule } from 'antd/es/form';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { QuestionCircleOutlined, SafetyCertificateOutlined, MailOutlined, MobileOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { registerDeveloper, sendVerificationCode } from '@/api/auth';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SliderCaptcha } from '@/components/ui/SliderCaptcha'; // 新增：导入滑块验证组件
import type { DeveloperRegisterRequest } from '@/types/auth';
import type { CaptchaToken } from '@/types/captcha'; // 新增：导入验证码token类型

/**
开发者注册表单组件（支持邮箱/手机 + 确认密码 + 滑块验证）
✅ 新增：滑块验证功能，发送验证码前需完成验证
✅ 新增：滑块验证成功后自动获取验证码
✅ 修复：每个渠道独立维护倒计时和验证状态，切换时状态保留
✅ 修复：未获取验证码时，验证码输入框禁止输入
✅ 修复：useEffect 依赖警告（使用函数式更新避免读取外部状态）
✅ 修复：TypeScript 严格模式（移除 any，使用 unknown + 类型守卫）
*/
export const RegisterForm = () => {
  const [form] = Form.useForm<DeveloperRegisterRequest & { confirm_password?: string }>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [sendingCode, setSendingCode] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  
  // 核心修复：每个渠道独立维护倒计时和验证状态
  const [countdowns, setCountdowns] = useState<{ email: number; sms: number }>({
    email: 0,
    sms: 0,
  });
  const [targetVerifiedMap, setTargetVerifiedMap] = useState<{ email: boolean; sms: boolean }>({
    email: false,
    sms: false,
  });
  
  // 缓存不同渠道已输入的账号，切换时保留
  const [cachedTargets, setCachedTargets] = useState<{ email: string; sms: string }>({
    email: '',
    sms: '',
  });

  console.log(cachedTargets);

  // 新增：滑块验证相关状态
  const isFirstRenderRef = useRef<boolean>(true);
  const [captchaVisible, setCaptchaVisible] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<CaptchaToken | null>(null);
  
  const navigate = useNavigate();
  
  // 计算当前渠道的状态（派生值，避免重复 state）
  const currentCountdown: number = countdowns[channel];
  const currentTargetVerified: boolean = targetVerifiedMap[channel];

  // 倒计时逻辑 - 全局定时器，同时递减两个渠道的倒计时
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdowns(prev => {
        const next = { ...prev };
        if (prev.email > 0) next.email = prev.email - 1;
        if (prev.sms > 0) next.sms = prev.sms - 1;
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 修复 1：切换渠道时保存/恢复输入值（使用函数式更新避免依赖警告）
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    
    const currentTarget = form.getFieldValue('target') as string | undefined;
    const otherChannel: 'email' | 'sms' = channel === 'email' ? 'sms' : 'email';

    // 使用函数式更新：在更新状态的同时立即更新表单
    // 这样就不需要读取外部的 cachedTargets，避免依赖警告
    setCachedTargets(prev => {
      const updated = {
        ...prev,
        [otherChannel]: currentTarget || '',
      };
      // 立即用更新后的值设置表单（避免读取外部 cachedTargets）
      form.setFieldsValue({ 
        target: updated[channel] || '',
        code: '' // 清空验证码，避免混淆
      });
      return updated;
    });
  }, [channel, form]); // 依赖数组不再包含 cachedTargets

  /**
  处理注册方式切换（用户手动点击时触发）
  */
  const handleChannelChange = useCallback((value: 'email' | 'sms') => {
    if (value === channel) return; // 避免重复切换
    setChannel(value);
    // 新增：切换渠道时重置当前渠道的验证码验证状态
    setTargetVerifiedMap(prev => ({ ...prev, [value]: false }));
  }, [channel]);

  // 新增：提取发送验证码逻辑为独立函数（避免闭包问题）
  const doSendCode = async () => {
    try {
      await form.validateFields(['target']);
      const target = form.getFieldValue('target') as string;
      setSendingCode(true);
      await sendVerificationCode({ target, channel });
      message.success(`验证码已发送至${channel === 'email' ? '邮箱' : '手机'}`);
      // 只更新当前渠道的倒计时和验证状态
      setCountdowns(prev => ({ ...prev, [channel]: 60 }));
      setTargetVerifiedMap(prev => ({ ...prev, [channel]: true }));
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      } else {
        message.error('发送验证码失败');
      }
    } finally {
      setSendingCode(false);
    }
  };

  /**
  发送验证码（新增滑块验证逻辑）
  */
  const handleSendCode = useCallback(async (): Promise<void> => {
    // 发送验证码前要求滑块验证
    if (!captchaToken?.verified) {
      setCaptchaVisible(true);
      message.warning('请先完成安全验证');
      return;
    }
    // 验证已通过，直接发送验证码
    await doSendCode();
  }, [captchaToken, channel, form]);

  // 新增：滑块验证成功回调
  const handleCaptchaSuccess = (token: CaptchaToken) => {
    setCaptchaToken(token);
    message.success('安全验证通过');
    // 验证成功后自动获取验证码（使用 setTimeout 确保状态更新）
    setTimeout(() => {
      doSendCode();
    }, 300);
  };

  // 新增：关闭滑块验证弹窗
  const handleCaptchaClose = useCallback(() => {
    setCaptchaVisible(false);
  }, []);

  /**
  确认密码的验证规则（纯前端验证）
  修复 2：使用 unknown + 类型守卫，移除 any
  */
  const validateConfirmPassword = async (_: unknown, value: unknown): Promise<void> => {
    const password = form.getFieldValue('password') as string | undefined;
    
    // 类型守卫：确保都是字符串再比较
    if (typeof value === 'string' && typeof password === 'string' && value !== password) {
      return Promise.reject(new Error('两次输入的密码不一致'));
    }
    return Promise.resolve();
  };

  /**
  处理表单提交
  */
  const onFinish = async (values: DeveloperRegisterRequest): Promise<void> => {
    // 使用当前渠道的验证状态
    if (!currentTargetVerified) {
      message.warning(`请先获取${channel === 'email' ? '邮箱' : '手机'}验证码`);
      return;
    }

    // 获取确认密码（前端验证用，不提交给后端）
    const allFields = form.getFieldsValue(true);
    const confirmPassword = allFields.confirm_password as string | undefined;

    // 前端二次确认密码一致性
    if (values.password !== confirmPassword) {
      message.warning('两次输入的密码不一致');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await registerDeveloper({
        target: values.target,
        channel: values.channel,
        code: values.code,
        password: values.password,
        name: values.name,
      });
      form.resetFields();
      message.success('注册成功，请登录。');
      navigate('/login');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '注册失败';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // 动态验证规则（根据渠道变化）
  const targetRules: Rule[] = useMemo(() => [
    { required: true, message: `请输入${channel === 'email' ? '邮箱' : '手机号'}` },
    channel === 'email'
      ? { type: 'email', message: '请输入有效的邮箱地址' }
      : { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的中国大陆手机号' },
  ], [channel]);

  return (
    <>
      {/* 错误提示 */}
      {error && (
        <ErrorAlert message="注册失败" description={error} />
      )}
      
      <Form
        form={form}
        name="register"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        className="space-y-8"
        autoComplete="off"
        initialValues={{ channel: 'email' }}
      >
        <div className="space-y-6">
          {/* 用户名输入框 */}
          <Form.Item
            name="name"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                用户名
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title="请输入您在平台使用的用户名">
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={[{ required: true, message: '请输入用户名' }]}
            className="mb-0"
          >
            <Input
              placeholder="请输入您的用户名"
              size="large"
              disabled={submitting}
              className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
          </Form.Item>

          {/* 注册方式选择 */}
          <Form.Item
            name="channel"
            label={
              <span className="font-medium text-gray-700">注册方式</span>
            }
            className="mb-0"
          >
            <Segmented
              options={[
                { label: '邮箱注册', value: 'email', icon: <MailOutlined /> },
                { label: '手机注册', value: 'sms', icon: <MobileOutlined /> },
              ]}
              value={channel}
              onChange={handleChannelChange}
              disabled={submitting}
              className="w-full"
            />
          </Form.Item>

          {/* 邮箱/手机号输入框 */}
          <Form.Item
            name="target"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                {channel === 'email' ? '注册邮箱' : '手机号码'}
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title={channel === 'email' ? '将作为登录账号' : '请输入中国大陆手机号'}>
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={targetRules}
            className="mb-0"
          >
            <Input
              placeholder={channel === 'email' ? '请输入有效邮箱地址' : '请输入中国大陆手机号'}
              size="large"
              // 优化：倒计时期间且已验证时才禁用，允许用户修改未验证的账号
              disabled={submitting || (currentCountdown > 0 && currentTargetVerified)}
              prefix={channel === 'email' ? <MailOutlined className="text-gray-400" /> : <MobileOutlined className="text-gray-400" />}
              className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
          </Form.Item>

          {/* 验证码输入框 + 发送按钮 */}
          <Form.Item
            name="code"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                {channel === 'email' ? '邮箱验证码' : '短信验证码'}
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title="请输入 6 位验证码">
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={[
              { required: true, message: '请输入验证码' },
              { len: 6, message: '验证码为 6 位数字' },
            ]}
            className="mb-0"
          >
            <div className="flex gap-2">
              <Input
                placeholder="请输入 6 位验证码"
                size="large"
                // 核心修复：未获取验证码时禁止输入
                disabled={submitting || !currentTargetVerified}
                prefix={<SafetyCertificateOutlined className="text-gray-400" />}
                className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors flex-1"
              />
              <Button
                type="primary"
                size="large"
                loading={sendingCode}
                // 使用当前渠道的倒计时
                disabled={submitting || currentCountdown > 0}
                onClick={handleSendCode}
                className="px-6 bg-linear-to-r from-blue-500 to-purple-600 border-0 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 rounded-lg whitespace-nowrap"
              >
                {/* 显示当前渠道的倒计时 */}
                {currentCountdown > 0 ? `${currentCountdown}s 后重发` : '获取验证码'}
              </Button>
            </div>
          </Form.Item>

          {/* 密码输入框 */}
          <Form.Item
            name="password"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                登录密码
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title="密码至少 6 位，建议字母 + 数字组合，妥善保管。">
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少 6 位' },
            ]}
            className="mb-0"
            hasFeedback
          >
            <Input.Password
              placeholder="请设置登录密码（至少 6 位）"
              size="large"
              disabled={submitting}
              autoComplete="new-password"
              className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
          </Form.Item>

          {/* 确认密码输入框（纯前端验证） */}
          <Form.Item
            name="confirm_password"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                确认密码
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title="请再次输入密码，确保一致。">
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={[
              { required: true, message: '请确认密码' },
              { validator: validateConfirmPassword },
            ]}
            className="mb-0"
            dependencies={['password']}
            hasFeedback
          >
            <Input.Password
              placeholder="请再次输入密码"
              size="large"
              disabled={submitting}
              autoComplete="new-password"
              className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
          </Form.Item>
        </div>

        {/* 提交按钮 */}
        <Form.Item className="mb-0 pt-2">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            disabled={submitting}
            className="w-full px-9 py-2.5 bg-linear-to-r from-blue-500 to-purple-600 border-0 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 rounded-lg"
          >
            {submitting ? '注册中...' : '完成注册'}
          </Button>
        </Form.Item>
      </Form>

      {/* 验证码说明 */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-xs text-blue-700 flex items-start gap-2">
          <SafetyCertificateOutlined className="mt-0.5 shrink-0" />
          <span>
            验证码将发送至您的{channel === 'email' ? '邮箱' : '手机'}，有效期 5 分钟。
            如未收到，请检查{channel === 'email' ? '垃圾邮件箱' : '短信拦截'}或 60 秒后重新获取。
          </span>
        </p>
      </div>

      {/* 新增：滑块验证组件 */}
      <SliderCaptcha
        visible={captchaVisible}
        scene="register" // 注册场景标识
        onSuccess={handleCaptchaSuccess}
        onClose={handleCaptchaClose}
      />
    </>
  );
};